import type { ConversionOptions, ConversionResult, ImageFormat } from '@/types';

const API_BASE = '/api'; // 使用相对路径，自动指向同域的 Pages Function

export class ImageConverter {
  private static instance: ImageConverter;

  static getInstance(): ImageConverter {
    if (!ImageConverter.instance) {
      ImageConverter.instance = new ImageConverter();
    }
    return ImageConverter.instance;
  }

  async convert(
    file: File,
    options: ConversionOptions,
    onProgress?: (progress: number) => void
  ): Promise<ConversionResult> {
    return new Promise(async (resolve, reject) => {
      try {
        onProgress?.(10);

        // 创建 FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('format', options.format);
        formData.append('quality', options.quality.toString());
        if (options.maxWidth) {
          formData.append('maxWidth', options.maxWidth.toString());
        }
        if (options.maxHeight) {
          formData.append('maxHeight', options.maxHeight.toString());
        }
        formData.append('keepAspectRatio', options.keepAspectRatio.toString());

        onProgress?.(30);

        // 调用 Worker API
        const response = await fetch(`${API_BASE}/convert`, {
          method: 'POST',
          body: formData,
        });

        onProgress?.(70);

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || '转换失败');
        }

        // 获取结果
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        // 从响应头获取元数据
        const originalSize = parseInt(response.headers.get('X-Original-Size') || file.size.toString());
        const compressedSize = parseInt(response.headers.get('X-Compressed-Size') || blob.size.toString());
        const compressionRatio = parseFloat(response.headers.get('X-Compression-Ratio') || '0');
        
        const contentDisposition = response.headers.get('Content-Disposition') || '';
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        const name = filenameMatch ? filenameMatch[1] : `converted.${options.format}`;

        onProgress?.(100);

        const result: ConversionResult = {
          blob,
          url,
          name,
          size: compressedSize,
          originalSize,
          compressionRatio,
        };

        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  }

  // 检查格式是否支持
  static isFormatSupported(format: ImageFormat): boolean {
    const supported = ['png', 'jpg', 'webp', 'avif', 'gif'];
    return supported.includes(format);
  }

  // 获取文件预览（本地生成）
  static async getPreview(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}